import { useMemo } from 'react';
import { useIsAdmin } from './authentication/authentication-service';
import { Location, useGetLocationListQuery, useGetLocationQuery, useGetMeQuery } from './gql/graphql.generated';
import { useLocationService } from './location/LocationService';

export const useMyLocations = () => {
  const [{ data: myData }] = useGetMeQuery();
  const [{ data: locationsData }] = useGetLocationListQuery();

  const myLocations = myData?.myEmployee?.locations || [];
  const allLocations = locationsData?.locations || [];
  const myId = myData?.myEmployee?.identityId;

  const isAdmin = useIsAdmin();

  return useMemo(() => {
    return allLocations.filter(location => {
      if (myLocations.find(myLoc => myLoc.id === location.id) || isAdmin) {
        return true;
      }

      const properties = JSON.parse(location.properties || '{}');
      if (properties.supervisorId === myId) {
        return true;
      }

      return false;
    });
  }, [allLocations, myLocations, isAdmin, myId]);
};

export const useAllLocations = () => {
  const [{ data: locationsData }] = useGetLocationListQuery();

  const locations: Location[] = useMemo(() => {
    let list: Location[] = [];

    if (locationsData) {
      list = locationsData.locations as Location[];
    }

    const activeLocations = list.filter(l => l.active);
    const inactiveLocations = list.filter(l => !l.active).map(l => ({ ...l, name: `${l.name} (Disabled)` }));
    list = [...activeLocations, ...inactiveLocations];

    return list;
  }, [locationsData]);

  return locations;
};

export const useMyLocation = () => {
  const [{ data: myData }] = useGetMeQuery();
  const selectedLocation = useLocationService(state => state.selectedLocation);

  if (selectedLocation) {
    return selectedLocation as Location;
  }

  const myLocationId = myData!.myEmployee?.homeLocationId;

  if (!myLocationId) {
    throw Error('User is not assigned to a location');
  }

  const [{ data: locationData }] = useGetLocationQuery({
    variables: { id: myLocationId }
  });

  const location = locationData!.location;

  if (!location) {
    throw Error("User's location not found");
  }

  return location as Location;
};

export const useLocation = (locationId: number) => {
  const selectedLocation = useLocationService(state => state.selectedLocation);
  const cachedLocation = selectedLocation?.id === locationId ? selectedLocation : undefined;

  const [{ data, fetching, error }, refetch] = useGetLocationQuery({
    variables: { id: locationId },
    pause: !!cachedLocation
  });

  const location = cachedLocation || data?.location;

  if (!location) {
    throw new Error(`Location with ID ${locationId} not found`);
  }

  return {
    location: location as Location,
    fetching: cachedLocation ? false : fetching,
    error,
    refetch
  };
};
